import { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';

interface AccountGateModalProps {
  onClose: () => void;
  onSuccess: () => void;
  onContinueAsGuest?: () => void;
}

type Mode = 'signup' | 'login';

export default function AccountGateModal({
  onClose,
  onSuccess,
  onContinueAsGuest,
}: AccountGateModalProps) {
  const { signUp, login } = useAuth();
  const [mode, setMode] = useState<Mode>('signup');
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (mode === 'signup' && !firstName.trim()) {
      setError('Please enter your first name.');
      return;
    }
    if (!email.trim()) {
      setError('Please enter your email address.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    setLoading(true);
    try {
      if (mode === 'signup') {
        await signUp(email.trim(), password, firstName.trim());
      } else {
        await login(email.trim(), password);
      }
      onSuccess();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
      if (msg.includes('already registered') || msg.includes('User already registered')) {
        setError('This email is already registered. Try logging in instead.');
      } else if (msg.includes('Invalid login credentials')) {
        setError('Incorrect email or password. Please try again.');
      } else if (msg.includes('Email not confirmed')) {
        setError('Please check your email to confirm your account first.');
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      <div
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-1 w-full bg-gradient-to-r from-pink-400 to-rose-500" />

        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-7 h-7 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full cursor-pointer transition-colors z-10"
        >
          <i className="ri-close-line text-gray-500 text-sm" />
        </button>

        <div className="px-7 pt-7 pb-8">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-8 h-8 flex items-center justify-center bg-pink-100 rounded-full shrink-0">
              <i className="ri-heart-fill text-pink-500 text-sm" />
            </div>
            <span className="text-[11px] font-bold text-pink-500 tracking-widest uppercase">
              The Mommy Mingle
            </span>
          </div>

          <h2
            className="text-xl font-bold text-gray-900 mb-1.5 leading-snug"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {mode === 'signup'
              ? 'Join The Mommy Mingle to Reserve Your Spot'
              : 'Welcome Back, Mama'}
          </h2>
          <p className="text-xs text-gray-500 mb-6 leading-relaxed">
            {mode === 'signup'
              ? 'Create a free account to RSVP and access events.'
              : 'Log in to continue to your RSVP.'}
          </p>

          <form onSubmit={handleSubmit} className="space-y-3.5">
            {mode === 'signup' && (
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  First Name
                </label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="e.g. Sarah"
                  className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-pink-300 focus:border-pink-300 transition-all"
                />
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-pink-300 focus:border-pink-300 transition-all"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={mode === 'signup' ? 'Create a password (6+ chars)' : 'Your password'}
                  className="w-full px-3.5 py-2.5 pr-10 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-pink-300 focus:border-pink-300 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className={showPassword ? 'ri-eye-off-line text-sm' : 'ri-eye-line text-sm'} />
                </button>
              </div>
            </div>

            {error && (
              <div className="flex items-start gap-2 text-xs text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">
                <i className="ri-error-warning-line text-sm shrink-0 mt-0.5" />
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-pink-500 hover:bg-pink-600 text-white text-sm font-bold rounded-xl transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 flex items-center justify-center gap-2 mt-1"
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin text-base" />
                  {mode === 'signup' ? 'Creating account...' : 'Logging in...'}
                </>
              ) : (
                <>
                  <i className="ri-checkbox-circle-line text-base" />
                  {mode === 'signup' ? 'Create Account & RSVP' : 'Log In & RSVP'}
                </>
              )}
            </button>
          </form>

          <p className="text-center text-xs text-gray-400 mt-4">
            {mode === 'signup' ? (
              <>
                Already a member?{' '}
                <button
                  onClick={() => {
                    setMode('login');
                    setError('');
                  }}
                  className="text-pink-500 font-semibold hover:text-pink-600 cursor-pointer"
                >
                  Log in
                </button>
              </>
            ) : (
              <>
                Don&apos;t have an account?{' '}
                <button
                  onClick={() => {
                    setMode('signup');
                    setError('');
                  }}
                  className="text-pink-500 font-semibold hover:text-pink-600 cursor-pointer"
                >
                  Sign up free
                </button>
              </>
            )}
          </p>

          {onContinueAsGuest && (
            <>
              <div className="mt-3 flex items-center gap-3">
                <div className="flex-1 h-px bg-gray-100" />
                <span className="text-[10px] text-gray-300 font-medium uppercase tracking-wide whitespace-nowrap">
                  or
                </span>
                <div className="flex-1 h-px bg-gray-100" />
              </div>
              <button
                type="button"
                onClick={onContinueAsGuest}
                className="w-full mt-2.5 py-2.5 border border-gray-200 hover:border-gray-300 text-gray-500 hover:text-gray-700 text-xs font-semibold rounded-xl transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5"
              >
                <i className="ri-eye-line text-gray-400 text-sm" />
                Continue as Guest
                <span className="text-gray-300 font-normal ml-0.5">— pricing locked</span>
              </button>
            </>
          )}

          <div className="mt-5 pt-4 border-t border-gray-100 flex items-center justify-center gap-4">
            <span className="flex items-center gap-1 text-[10px] text-gray-400">
              <i className="ri-lock-line text-gray-300" />
              Secure &amp; private
            </span>
            <span className="text-gray-200 text-xs">·</span>
            <span className="flex items-center gap-1 text-[10px] text-gray-400">
              <i className="ri-gift-line text-gray-300" />
              Free to join
            </span>
            <span className="text-gray-200 text-xs">·</span>
            <span className="flex items-center gap-1 text-[10px] text-gray-400">
              <i className="ri-spam-3-line text-gray-300" />
              No spam
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
